"use client";
import { useState, Suspense } from "react";
import { useAuth } from "@/context/AuthContext";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
    User, Heart, ShoppingBag, Search, ClipboardList, MapPin, Store,
    Headphones, Info, FileText, RotateCcw, LogOut, Trash2, ChevronRight, Gift, Smartphone, Package
} from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import PhoneAuthModal from "@/components/PhoneAuthModal";
import ContactUsModal from "@/components/ContactUsModal";

const AccountContent = () => {
    const { user, profile, loading, login, signup, logout } = useAuth();
    const router = useRouter();
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [phoneModalOpen, setPhoneModalOpen] = useState(false);
    const [contactModalOpen, setContactModalOpen] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            if (isLogin) {
                await login(email, password);
                toast.success("Welcome back! 🎉");
            } else {
                await signup(email, password, name);
                toast.success("Account created successfully!");
            }
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : "Authentication failed";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20 min-h-[50vh]">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    if (!user) {
        return (
            <div className="max-w-sm mx-auto py-8 space-y-6">
                <div className="text-center space-y-2">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto">
                        <User className="w-8 h-8 text-primary" />
                    </div>
                    <h1 className="text-xl font-bold text-foreground">{isLogin ? "Welcome Back" : "Create Account"}</h1>
                    <p className="text-sm text-muted-foreground">
                        {isLogin ? "Login to access your cart, orders & more" : "Sign up to start ordering fresh meat"}
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {!isLogin && (
                        <input
                            type="text"
                            placeholder="Full Name"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            required
                            className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                        />
                    )}
                    <input
                        type="email"
                        placeholder="Email Address"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <input
                        type="password"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        minLength={6}
                        className="w-full px-4 py-3 border border-border rounded-lg bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    />
                    <button
                        type="submit"
                        disabled={submitting}
                        className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold text-sm disabled:opacity-50 transition-opacity"
                    >
                        {submitting ? "Please wait..." : isLogin ? "Login" : "Sign Up"}
                    </button>
                </form>

                {/* OR divider */}
                <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-xs text-muted-foreground font-medium">OR</span>
                    <div className="flex-1 h-px bg-border" />
                </div>

                {/* Phone OTP button */}
                <button
                    onClick={() => setPhoneModalOpen(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-primary/30
            rounded-lg text-sm font-semibold text-primary hover:bg-primary/5 transition-colors"
                >
                    <Smartphone className="w-4 h-4" />
                    Login with Mobile OTP
                </button>

                <p className="text-center text-sm text-muted-foreground">
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                    <button onClick={() => setIsLogin(!isLogin)} className="text-primary font-medium">
                        {isLogin ? "Sign Up" : "Login"}
                    </button>
                </p>

                {/* Phone Auth Modal */}
                <PhoneAuthModal open={phoneModalOpen} onClose={() => setPhoneModalOpen(false)} />
            </div>
        );
    }

    const menuItems = [
        {
            section: "My Activity", items: [
                { icon: Package, label: "Active Orders", to: "/active-orders" },
                { icon: ClipboardList, label: "Order History", to: "/orders" },
                { icon: MapPin, label: "Address Book", to: "/address-book" },
                {
                    icon: Store, label: "Change Outlet", action: () => {
                        if (typeof window !== 'undefined') {
                            window.dispatchEvent(new CustomEvent("openOutletPicker"));
                        }
                    }
                },
            ]
        },
        {
            section: "Support & About", items: [
                { icon: Headphones, label: "Contact Us", action: () => setContactModalOpen(true) },
                { icon: Info, label: "About Us", to: "/about-us" },
                { icon: FileText, label: "Terms & Conditions", to: "/terms-and-conditions" },
                { icon: RotateCcw, label: "Refund Policy", to: "/refund-policy" },
            ]
        },
    ];

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6 pb-20">
            {/* Profile card */}
            <div className="bg-card rounded-xl p-5 border border-border shadow-sm">
                <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-full border-2 border-foreground flex items-center justify-center font-bold text-xl text-foreground">
                        {profile?.name?.[0]?.toUpperCase() || user.email?.[0]?.toUpperCase() || user.phoneNumber?.[3]?.toUpperCase() || "U"}
                    </div>
                    <div>
                        <h2 className="font-semibold text-foreground">{profile?.name || (user.phoneNumber ? "Mobile User" : "User")}</h2>
                        <p className="text-sm text-muted-foreground">{user.email || user.phoneNumber || ""}</p>
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-2 bg-muted rounded-lg px-4 py-2.5">
                    <Gift className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Reward Points</span>
                    <span className="ml-auto font-bold text-foreground">{profile?.rewardPoints?.toFixed(2) || "0.00"}</span>
                </div>
            </div>

            {/* Quick actions */}
            <div className="grid grid-cols-3 gap-3">
                {[
                    { icon: Heart, label: "Favourites", color: "text-primary", to: "/favourites" },
                    { icon: ShoppingBag, label: "Cart", color: "text-blue-500", to: "/cart" },
                    { icon: Search, label: "Search", color: "text-success", to: "/" },
                ].map((action) => (
                    <Link
                        key={action.label}
                        href={action.to}
                        className="flex flex-col items-center gap-2 bg-muted/50 rounded-xl py-4 hover:bg-muted transition-colors"
                    >
                        <action.icon className={`w-6 h-6 ${action.color}`} />
                        <span className="text-xs font-medium text-foreground">{action.label}</span>
                    </Link>
                ))}
            </div>

            {/* Menu sections */}
            {menuItems.map((section) => (
                <div key={section.section} className="space-y-2">
                    <h3 className="font-semibold text-foreground text-sm">{section.section}</h3>
                    <div className="bg-card rounded-xl border border-border overflow-hidden divide-y divide-border">
                        {section.items.map((item) => (
                            item.action ? (
                                <button
                                    key={item.label}
                                    onClick={item.action}
                                    className="w-full flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors"
                                >
                                    <item.icon className="w-5 h-5 text-muted-foreground" />
                                    <span className="flex-1 text-left text-sm text-foreground">{item.label}</span>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                </button>
                            ) : (
                                <Link
                                    key={item.label}
                                    href={item.to || "/account"}
                                    className="flex items-center gap-3 px-4 py-3.5 hover:bg-muted/50 transition-colors"
                                >
                                    <item.icon className="w-5 h-5 text-muted-foreground" />
                                    <span className="flex-1 text-sm text-foreground">{item.label}</span>
                                    <ChevronRight className="w-4 h-4 text-muted-foreground" />
                                </Link>
                            )
                        ))}
                    </div>
                </div>
            ))}

            {/* Logout */}
            <button
                onClick={async () => {
                    await logout();
                    toast.success("Logged out successfully");
                }}
                className="w-full flex items-center gap-3 bg-card rounded-xl border border-border px-4 py-3.5 hover:bg-muted/50 transition-colors"
            >
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                    <LogOut className="w-4 h-4 text-primary" />
                </div>
                <div className="text-left">
                    <p className="text-sm font-medium text-foreground">Logout</p>
                    <p className="text-xs text-muted-foreground">Sign out from your account</p>
                </div>
            </button>

            {/* Modals */}
            <ContactUsModal open={contactModalOpen} onClose={() => setContactModalOpen(false)} />
        </motion.div>
    );
};

export default function AccountPage() {
    return (
        <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>}>
            <AccountContent />
        </Suspense>
    );
}
